// T050 — useTheme() hook: localStorage + prefers-color-scheme, toggles 'dark' class on <html>
// Matches the CSS class-based dark switching: @custom-variant dark (&:is(.dark *))
import { useState, useEffect, useCallback } from "react";

type ThemePreference = "light" | "dark" | "system";
type EffectiveTheme = "light" | "dark";

interface UseThemeReturn {
  preference: ThemePreference;
  effective: EffectiveTheme;
  setPreference: (pref: ThemePreference) => void;
}

const STORAGE_KEY = "learn.theme";

function getStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage unavailable (SSR or private mode)
  }
  return "system";
}

function getSystemDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveEffective(pref: ThemePreference, systemDark: boolean): EffectiveTheme {
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  return systemDark ? "dark" : "light";
}

export function useTheme(): UseThemeReturn {
  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference);
  const [systemDark, setSystemDark] = useState<boolean>(getSystemDark);

  const effective = resolveEffective(preference, systemDark);

  // Apply 'dark' class to <html> on every change to effective theme.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", effective === "dark");
  }, [effective]);

  // Listen for OS-level prefers-color-scheme changes when preference is "system".
  useEffect(() => {
    if (preference !== "system") return;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const setPreference = useCallback((pref: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // localStorage unavailable
    }
    setPreferenceState(pref);
    // Re-read system dark in case it changed while we were on a fixed pref.
    if (pref === "system") {
      setSystemDark(getSystemDark());
    }
  }, []);

  return { preference, effective, setPreference };
}
